import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { updateCartDetails } from '../redux/actions/CartActions';

export const useCartCalculations = () => {
  const dispatch = useDispatch();
  const endorsements = useSelector((state) => state.wizardLaunch?.endorsements) || [];
  const moreClassSelection = useSelector((state) => state.wizardLaunch?.moreClassSelection) || [];
  const directCourses = useSelector((state) => state.wizardLaunch?.directCourses) || [];
  const directAccess = useSelector((state) => state.wizardLaunch?.directAccess) || [];
  const dynamicData = useSelector((state) => state.wizardLaunch.dynamicData);

  const visibleEndorsements = endorsements.filter(endorsement => 
    !endorsement.hiddenInEndorsements || endorsement.hiddenInEndorsements === "0"
  );

  const visibleMoreClassSelection = moreClassSelection.filter(course => 
    !course.hiddenInEndorsements || course.hiddenInEndorsements === "0"
  );

  // Use directAccess.data.offer if available, otherwise use dynamicData.offer
  const offerAmount = directAccess?.data?.offer || dynamicData?.offer || "$0";
  const coupon = useSelector(state => state.cart.coupon);

  // Filter out Instructor-Led courses, only calculate for Self-Paced
  const selfPacedCourses = visibleMoreClassSelection.filter(course => course.courseType === "Self-Paced") || [];
  const hasInstructorLed = visibleMoreClassSelection.some(course => course.courseType === "Instructor-Led");
  
  let subtotal = 0;
  
  // Calculate subtotal from ALL endorsements
  endorsements.forEach((product) => {
    const price = parseFloat(product.price) || 0;
    if (!isNaN(price)) {
      subtotal += price;
    }
  });

  // Calculate subtotal from ALL self-paced courses
  moreClassSelection.filter(course => course.courseType === "Self-Paced").forEach((course) => {
    const price = parseFloat(course.price) || 0;
    if (!isNaN(price)) {
      subtotal += price;
    }
  });

  // Calculate subtotal from ALL direct courses
  directCourses.forEach((course) => {
    const price = parseFloat(course.price) || 0;
    if (!isNaN(price)) {
      subtotal += price;
    }
  });
  
  // Calculate multi-selection discount only for visible items
  const numericOfferAmount = parseFloat(offerAmount.replace('$', '')) || 0;
const visibleEndorsementsCount = visibleEndorsements.length;
const visibleMoreClassCount = visibleMoreClassSelection.filter(course => course.courseType === "Self-Paced").length;
const visibleDirectCoursesCount = directCourses.length;

const additionalEndorsements = Math.max(0, visibleEndorsementsCount - 1);
const additionalCourses = Math.max(0, visibleMoreClassCount - 1);
const additionalDirectCourses = Math.max(0, visibleDirectCoursesCount - 1);
let calculatedCourseDiscount = (additionalEndorsements + additionalCourses + additionalDirectCourses) * numericOfferAmount;

  let calculatedCouponDiscount = 0;

  // Add course-specific discounts (only for self-paced courses)
  selfPacedCourses?.forEach((course) => {
   if(!course.hiddenInEndorsements || course.hiddenInEndorsements === "0"){
    if (course.discount) {
      const discount = parseFloat(course.discount) || 0;
      if (!isNaN(discount)) {
        calculatedCourseDiscount += discount;
      }
    }
   }
  });

  calculatedCouponDiscount = calculatedCourseDiscount;

  if (coupon) {
    const couponDiscount = parseFloat(coupon.discount) || 0;
    if (!isNaN(couponDiscount)) {
      calculatedCouponDiscount += couponDiscount;
    }
  }

  // Ensure we don't have negative values
  subtotal = Math.max(0, subtotal);
  calculatedCourseDiscount = Math.max(0, Math.min(calculatedCourseDiscount, subtotal));
  const total = Math.max(0, subtotal - calculatedCouponDiscount)?.toFixed(2);

  // Update cart details in Redux
  useEffect(() => {
    const visibleSelfPacedCourses = selfPacedCourses.filter(course => 
      !course.hiddenInEndorsements || course.hiddenInEndorsements === "0"
    );

    const visibleEndorsementsForDiscount = endorsements.filter(endorsement => 
      !endorsement.hiddenInEndorsements || endorsement.hiddenInEndorsements === "0"
    );

    dispatch(updateCartDetails({
      subtotal: subtotal?.toFixed(2),
      discount: calculatedCourseDiscount?.toFixed(2),
      total,
      hasInstructorLed,
      selfPacedCourses: visibleSelfPacedCourses,
      endorsements: visibleEndorsementsForDiscount,
      moreClassSelection,
      directCourses
    }));
  }, [total, dispatch]);

  return {
    subtotal: subtotal?.toFixed(2),
    discount: calculatedCourseDiscount?.toFixed(2),
    total,
    hasInstructorLed,
    moreClassSelection: visibleMoreClassSelection,
    endorsements: visibleEndorsements,
    directCourses
  };
};
